import React, { useEffect, useMemo, useState } from 'react';
import { buildShareUrl } from '../utils/shareLinks';
import { normaliseEmail } from '../utils/accessCredentials';
import { normalisePhoneNumber, phoneNumberForWhatsApp } from '../utils/phoneNumbers';

const copyToClipboard = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'absolute';
  textArea.style.left = '-9999px';
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand('copy');
  document.body.removeChild(textArea);
};

const buildWhatsAppUrl = (text, phone = '') => {
  const whatsappPhone = phoneNumberForWhatsApp(phone);
  const encodedText = encodeURIComponent(text);
  return whatsappPhone
    ? `https://wa.me/${whatsappPhone}?text=${encodedText}`
    : `https://wa.me/?text=${encodedText}`;
};

const isInviteRequest = (share) => (
  share.type === 'invite-request' ||
  ['Invite request', 'Waiting list'].includes(share.access)
);
const isPendingInviteRequest = (share) => isInviteRequest(share) && !share.publicAccess;

function SharesPage({ links, shares, onCreateShare }) {
  const [draft, setDraft] = useState({
    linkId: links[0]?.id || '',
    emailInput: '',
    allowedEmails: [],
    channel: 'Email',
    password: '',
    phoneInput: '',
  });
  const [copyStatus, setCopyStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const selectedLink = useMemo(
    () => links.find((item) => item.id === draft.linkId) || links[0],
    [draft.linkId, links],
  );

  const selectedShareUrl = buildShareUrl(selectedLink);
  const isCredentialAccess = ['Email', 'WhatsApp'].includes(draft.channel);
  const isRespondingToRequest = Boolean(selectedRequest?.id);

  const addAllowedEmail = () => {
    const email = normaliseEmail(draft.emailInput);
    if (!email || draft.allowedEmails.includes(email)) {
      setDraft((current) => ({ ...current, emailInput: '' }));
      return;
    }

    setDraft((current) => ({
      ...current,
      emailInput: '',
      allowedEmails: [...current.allowedEmails, email],
    }));
  };

  const removeAllowedEmail = (email) => {
    setDraft((current) => ({
      ...current,
      allowedEmails: current.allowedEmails.filter((item) => item !== email),
    }));
  };

  useEffect(() => {
    if (!draft.linkId && links[0]?.id) {
      setDraft((current) => ({ ...current, linkId: links[0].id }));
    }
  }, [draft.linkId, links]);

  const createSharePayload = (allowedEmails = draft.allowedEmails) => ({
    requestId: selectedRequest?.id || '',
    linkId: selectedLink?.id || draft.linkId,
    recipient: isCredentialAccess ? allowedEmails.join(', ') : 'Copied link',
    allowedEmails: isCredentialAccess ? allowedEmails : [],
    channel: draft.channel,
    access: isCredentialAccess ? 'Email + password' : selectedLink?.protection || 'Invite only',
    status: isCredentialAccess ? 'Access created' : 'Copied',
    shareUrl: selectedShareUrl,
    password: isCredentialAccess ? draft.password.trim() : '',
    hasPassword: isCredentialAccess && Boolean(draft.password.trim()),
    phone: normalisePhoneNumber(draft.phoneInput || selectedRequest?.phone || ''),
  });

  const selectInviteRequest = (share) => {
    if (!isPendingInviteRequest(share)) return;

    const email = normaliseEmail(share.recipientEmail || share.recipient);
    setSelectedRequest(share);
    setCopyStatus('');
    setDraft((current) => ({
      ...current,
      linkId: share.linkId || current.linkId,
      emailInput: email ? '' : current.emailInput,
      allowedEmails: email ? [email] : [],
      channel: share.phone ? 'WhatsApp' : 'Email',
      password: '',
      phoneInput: normalisePhoneNumber(share.phone || ''),
    }));
  };

  const clearSelectedRequest = () => {
    setSelectedRequest(null);
    setCopyStatus('');
  };

  const handleCreateShare = async (shouldCopy = false) => {
    if (!selectedLink || isSubmitting) return;

    setCopyStatus('');
    const typedEmail = normaliseEmail(draft.emailInput);
    const allowedEmails = isCredentialAccess && typedEmail && !draft.allowedEmails.includes(typedEmail)
      ? [...draft.allowedEmails, typedEmail]
      : draft.allowedEmails;

    if (isCredentialAccess && (!allowedEmails.length || !draft.password.trim())) {
      setCopyStatus('Add at least one allowed email and enter the shared password.');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = createSharePayload(allowedEmails);
      const createdShare = await onCreateShare(payload);
      if (shouldCopy) {
        const copiedText = payload.hasPassword
          ? `${payload.shareUrl}\nPassword: ${payload.password}`
          : payload.shareUrl;
        await copyToClipboard(copiedText);
        setCopyStatus(createdShare ? 'Share link copied.' : 'Link copied.');
      }
      setDraft((current) => ({
        ...current,
        allowedEmails: isCredentialAccess ? [] : current.allowedEmails,
        emailInput: '',
        channel: shouldCopy ? 'Copy link' : current.channel,
        password: isCredentialAccess ? '' : current.password,
      }));
      if (isRespondingToRequest) {
        setSelectedRequest(null);
        setCopyStatus(shouldCopy ? 'Invite updated and link copied.' : 'Invite sent.');
      }
    } catch (error) {
      setCopyStatus(error.message || 'Unable to copy share link.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsAppShare = async () => {
    if (!selectedLink || isSubmitting) return;

    setCopyStatus('');
    const typedEmail = normaliseEmail(draft.emailInput);
    const allowedEmails = typedEmail && !draft.allowedEmails.includes(typedEmail)
      ? [...draft.allowedEmails, typedEmail]
      : draft.allowedEmails;

    if (!allowedEmails.length || !draft.password.trim()) {
      setCopyStatus('Add at least one allowed email and enter the shared password.');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = createSharePayload(allowedEmails);
      await onCreateShare({ ...payload, channel: 'WhatsApp' });
      window.open(buildWhatsAppUrl(`${payload.shareUrl}\nPassword: ${payload.password}`, payload.phone), '_blank', 'noopener,noreferrer');
      setCopyStatus('WhatsApp invite opened.');
      setDraft((current) => ({
        ...current,
        allowedEmails: [],
        emailInput: '',
        channel: 'WhatsApp',
        password: '',
        phoneInput: '',
      }));
      if (isRespondingToRequest) {
        setSelectedRequest(null);
      }
    } catch (error) {
      setCopyStatus(error.message || 'Unable to create WhatsApp invite.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="mx-auto max-w-7xl px-5 py-6 sm:px-8">
      <div className="mb-6">
        <p className="label-caps">Link shares</p>
        <h1 className="mt-2 text-3xl font-semibold tracking-normal">Recipients and delivery</h1>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
          Create email and password access records for protected links. No email is sent from Sharabl.
        </p>
      </div>

      <div className="mb-5 grid gap-4 lg:grid-cols-[1fr_340px]">
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div className="grid share-row-header">
            <span>Recipient</span>
            <span className="hidden md:block">Link</span>
            <span className="hidden md:block">Channel</span>
            <span>Status</span>
          </div>
          {shares.map((share) => {
            const link = links.find((item) => item.id === share.linkId);
            const pendingRequest = isPendingInviteRequest(share);
            const respondedRequest = isInviteRequest(share) && share.publicAccess;
            const rowClassName = [
              'share-row',
              pendingRequest ? 'cursor-pointer bg-amber-50/70 hover:bg-amber-50' : '',
              respondedRequest ? 'bg-emerald-50/60' : '',
              selectedRequest?.id === share.id ? 'ring-2 ring-inset ring-amber-300' : '',
            ].filter(Boolean).join(' ');
            const statusClassName = respondedRequest
              ? 'rounded bg-emerald-100 px-2 py-1 text-xs font-semibold uppercase text-emerald-700'
              : pendingRequest
                ? 'rounded bg-amber-100 px-2 py-1 text-xs font-semibold uppercase text-amber-800'
                : 'rounded bg-slate-100 px-2 py-1 text-xs font-semibold uppercase text-slate-600';

            return (
              <div
                className={rowClassName}
                key={share.id}
                onClick={() => selectInviteRequest(share)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    selectInviteRequest(share);
                  }
                }}
                role={pendingRequest ? 'button' : undefined}
                tabIndex={pendingRequest ? 0 : undefined}
              >
                <span>
                  <strong className="block text-sm font-semibold text-slate-950">{share.recipient}</strong>
                  <span className="block text-xs text-slate-500">
                    {share.access}
                  </span>
                  {share.phone && (
                    <span className="block text-xs text-slate-500">{share.phone}</span>
                  )}
                  {share.reason && (
                    <span className="mt-1 block text-xs leading-5 text-slate-500">{share.reason}</span>
                  )}
                </span>
                <span className="hidden text-sm text-slate-600 md:block">{link?.title || 'Deleted link'}</span>
                <span className="hidden text-sm text-slate-600 md:block">{share.channel}</span>
                <span className="flex flex-wrap items-center gap-2 justify-self-start">
                  <span className={statusClassName}>
                    {share.status}
                  </span>
                  {pendingRequest && (
                    <span className="rounded border border-amber-300 bg-white px-2 py-1 text-xs font-semibold uppercase text-amber-800">
                      Send invite
                    </span>
                  )}
                  {share.shareUrl && (
                    <a
                      className="rounded border border-slate-300 bg-white px-2 py-1 text-xs font-semibold uppercase text-slate-600 transition hover:border-slate-950 hover:text-slate-950"
                      href={buildWhatsAppUrl(share.shareUrl, share.phone)}
                      rel="noreferrer"
                      target="_blank"
                    >
                      WhatsApp
                    </a>
                  )}
                </span>
              </div>
            );
          })}
        </div>

        <form
          className="rounded-lg border border-slate-200 bg-white p-5"
          onSubmit={async (event) => {
            event.preventDefault();
            await handleCreateShare(false);
          }}
        >
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <p className="label-caps">
                {isRespondingToRequest
                  ? selectedRequest?.access === 'Waiting list' ? 'Respond to waiting list' : 'Respond to request'
                  : 'Create share'}
              </p>
              {isRespondingToRequest && (
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  {selectedRequest.recipientEmail || selectedRequest.recipient}
                </p>
              )}
            </div>
            {isRespondingToRequest && (
              <button
                className="rounded border border-slate-300 bg-white px-2 py-1 text-xs font-semibold uppercase text-slate-600 transition hover:border-slate-950 hover:text-slate-950"
                onClick={clearSelectedRequest}
                type="button"
              >
                Clear
              </button>
            )}
          </div>
          <label className="field-label mt-0" htmlFor="share-link">Link</label>
          <select
            className="field"
            id="share-link"
            value={draft.linkId}
            onChange={(event) => setDraft({ ...draft, linkId: event.target.value })}
            disabled={!links.length}
          >
            {links.map((link) => (
              <option key={link.id} value={link.id}>{link.title}</option>
            ))}
          </select>

          <label className="field-label" htmlFor="channel">Channel</label>
          <select
            className="field"
            id="channel"
            value={draft.channel}
            onChange={(event) => setDraft({ ...draft, channel: event.target.value })}
          >
            <option>Email</option>
            <option>WhatsApp</option>
            <option>Copy link</option>
          </select>

          {isCredentialAccess && (
            <>
              <label className="field-label" htmlFor="allowed-email">Allowed emails</label>
              <div className="flex gap-2">
                <input
                  className="field"
                  id="allowed-email"
                  placeholder="client@example.com"
                  type="email"
                  value={draft.emailInput}
                  onChange={(event) => setDraft({ ...draft, emailInput: event.target.value })}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      addAllowedEmail();
                    }
                  }}
                />
                <button
                  className="shrink-0 rounded border border-slate-950 bg-white px-3 py-2 text-sm font-semibold text-slate-950 transition hover:bg-slate-950 hover:text-white"
                  onClick={addAllowedEmail}
                  type="button"
                >
                  Add
                </button>
              </div>
              {draft.allowedEmails.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {draft.allowedEmails.map((email) => (
                    <span className="chip inline-flex items-center gap-2" key={email}>
                      {email}
                      <button
                        aria-label={`Remove ${email}`}
                        className="text-slate-500 transition hover:text-slate-950"
                        onClick={() => removeAllowedEmail(email)}
                        type="button"
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <label className="field-label" htmlFor="share-password">Password</label>
              <input
                className="field"
                id="share-password"
                placeholder="Enter access password"
                type="text"
                value={draft.password}
                onChange={(event) => setDraft({ ...draft, password: event.target.value })}
              />
              {draft.channel === 'WhatsApp' && (
                <>
                  <label className="field-label" htmlFor="share-phone">WhatsApp number</label>
                  <input
                    className="field"
                    id="share-phone"
                    placeholder="+27 82 123 4567"
                    type="tel"
                    value={draft.phoneInput}
                    onBlur={() => setDraft((current) => ({
                      ...current,
                      phoneInput: normalisePhoneNumber(current.phoneInput),
                    }))}
                    onChange={(event) => setDraft({ ...draft, phoneInput: event.target.value })}
                  />
                </>
              )}
            </>
          )}

          <div className="mt-4 rounded border border-slate-200 bg-slate-50 p-3">
            <p className="text-xs text-slate-500">Share URL</p>
            <p className="mt-1 break-all text-sm font-semibold text-slate-950">{selectedShareUrl}</p>
          </div>

          {copyStatus && (
            <p className="mt-3 text-sm font-semibold text-slate-600">{copyStatus}</p>
          )}

          <button
            className="mt-4 w-full rounded bg-slate-950 px-4 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
            type="submit"
            disabled={!links.length || isSubmitting}
          >
            {isSubmitting ? 'Saving access...' : isRespondingToRequest ? 'Send invite' : isCredentialAccess ? 'Create access' : 'Create share'}
          </button>
          {isCredentialAccess && (
            <button
              className="mt-2 w-full rounded border border-emerald-700 bg-white px-4 py-3 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-700 hover:text-white disabled:cursor-not-allowed disabled:border-slate-300 disabled:text-slate-300 disabled:hover:bg-white"
              type="button"
              disabled={!links.length || isSubmitting}
              onClick={handleWhatsAppShare}
            >
              Share with WhatsApp
            </button>
          )}
          <button
            className="mt-2 w-full rounded border border-slate-950 bg-white px-4 py-3 text-sm font-semibold text-slate-950 transition hover:bg-slate-950 hover:text-white disabled:cursor-not-allowed disabled:border-slate-300 disabled:text-slate-300 disabled:hover:bg-white"
            type="button"
            disabled={!links.length || isSubmitting}
            onClick={() => handleCreateShare(true)}
          >
            Copy share link
          </button>
        </form>
      </div>
    </section>
  );
}

export default SharesPage;
